/**
 * @page /portal
 * @description Mitarbeiter-Portal Dashboard — Persoenliche Uebersicht fuer Mitarbeiter
 *   mit Begruessung, Schichtplan, News-Ticker und Quick-Links.
 * @features
 *   - Tagesaktuelle Begruessung mit Uhrzeit
 *   - Aktuelle Schicht-Anzeige mit Farbkodierung
 *   - Neueste Firmennews als Ticker
 *   - Quick-Navigation zu Portal-Unterseiten
 * @api GET /api/portal/ma-dashboard
 * @roles mitarbeiter, admin, manager
 * @i18n portal.dashboard
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Clock, Umbrella, Newspaper, FileText, Calendar, Pin, AlertCircle, Target, MapPin, Coffee, UtensilsCrossed, LogOut, LogIn, Pause, CheckCircle, AlertTriangle } from 'lucide-react';
import { Card, KpiCard, Badge, Button } from '../components/ui';
import { berechnePlanbarAnteile } from '../utils/urlaubPlanbar';

const API = import.meta.env.VITE_API_URL || '';

function fmtDateShortP(dateStr) {
  if (!dateStr) return '';
  const d = new Date(String(dateStr).slice(0, 10) + 'T00:00:00');
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

const heute = new Date();
const uhrzeit = heute.getHours();

function getGreeting(t) {
  if (uhrzeit < 12) return t('portal.dashboard.goodMorning');
  if (uhrzeit < 18) return t('portal.dashboard.goodDay');
  return t('portal.dashboard.goodEvening');
}

function formatDatum(d, t) {
  const date = new Date(d);
  return t('portal.dashboard.dateFormat', {
    weekday: t(`portal.dashboard.weekdays.${date.getDay()}`),
    day: date.getDate(),
    month: t(`portal.dashboard.months.${date.getMonth()}`),
    year: date.getFullYear(),
  });
}

// Schichtfarben
const schichtFarben = {
  'Frühschicht': { bg: 'bg-primary-500/10 dark:bg-primary-900/20', text: 'text-primary-400', border: 'border-primary-500/20', variant: 'info' },
  'Spätschicht': { bg: 'bg-orange-500/10 dark:bg-orange-900/20', text: 'text-orange-400', border: 'border-orange-500/20', variant: 'warning' },
  'Nachtschicht': { bg: 'bg-purple-500/10 dark:bg-purple-900/20', text: 'text-purple-400', border: 'border-purple-500/20', variant: 'default' },
};

function getSchichtFarbe(name) {
  if (!name) return { bg: 'bg-[var(--bg-inset,#F8F9FC)]', text: 'text-[var(--text-tertiary,#6B7280)]', border: 'border-[var(--border-default,#E5E7EB)]', variant: 'default' };
  for (const [key, val] of Object.entries(schichtFarben)) {
    if (name.toLowerCase().includes(key.toLowerCase().replace('schicht', ''))) return val;
  }
  return { bg: 'bg-primary-500/10 dark:bg-primary-900/20', text: 'text-primary-400', border: 'border-primary-500/20', variant: 'info' };
}

// ─── Stempel-Widget ───

function StempelWidget({ t }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [position, setPosition] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const timerRef = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/stempel/mein-status`, { credentials: 'include' });
      if (res.ok) setStatus(await res.json());
    } catch { /* */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Auto-refresh every 30s
  useEffect(() => {
    const iv = setInterval(loadStatus, 30000);
    return () => clearInterval(iv);
  }, [loadStatus]);

  // Live timer
  useEffect(() => {
    if (status?.status === 'eingestempelt' || status?.status === 'in_pause') {
      timerRef.current = setInterval(() => setStatus(s => ({ ...s })), 15000);
      return () => clearInterval(timerRef.current);
    }
  }, [status?.status]);

  // #1813 — Position nur anfordern, wenn fuer diese Person Standortpflicht gilt.
  // Vorher fragte die Seite JEDEN nach dem Standort und zeigte bei Ablehnung einen roten
  // Hinweis, obwohl die Position gar nicht gebraucht wurde.
  const standortPflicht = status?.standort_pflicht === true;

  useEffect(() => {
    if (!standortPflicht) { setGpsError(null); setPosition(null); return; }
    if (!navigator.geolocation) { setGpsError(t('stempel.app.gpsNichtUnterstuetzt', 'Geolocation nicht unterstützt')); return; }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setPosition({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy }); setGpsLoading(false); },
      (err) => {
        setGpsError(err.code === 1 ? t('stempel.app.gpsVerweigert') : t('stempel.app.gpsNichtVerfuegbar', 'Standort nicht verfügbar'));
        setGpsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }, [t, standortPflicht]);

  const handleStempel = async (aktion) => {
    setActionLoading(aktion);
    setToast(null);
    try {
      const body = { aktion };
      if (position) { body.latitude = position.latitude; body.longitude = position.longitude; }
      const res = await fetch(`${API}/api/stempel/app-scan`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      // Ein gebuchter Stempel ist die EINZIGE Erfolgsbedingung: `erfolg: true` sendet der
      // Server ausschliesslich nach dem INSERT. Der Endpoint kennt weitere 200er, die KEIN
      // Stempel sind — `blocked` (Ganztags-Abwesenheit #822, ausserhalb Arbeitszeitmodell
      // #848), `rule_blocked` und `requires_reason`. Ein blosses `res.ok` meldete die
      // faelschlich als gebucht (#1812-Fix).
      if (res.ok && data.erfolg === true) {
        if (data.gps_ausserhalb) {
          setToast({ type: 'warning', text: t('stempel.app.standortWarnung', { distanz: fmtDistanz(data.gps_distanz_meter) }) });
        } else {
          setToast({ type: 'success', text: data.nachricht });
        }
        loadStatus();
      } else {
        setToast({ type: 'error', text: data.rule_meldung || data.warning || data.error || data.hinweis || data.message || 'Fehler' });
      }
    } catch {
      setToast({ type: 'error', text: 'Netzwerkfehler' });
    } finally {
      setActionLoading(null);
    }
  };

  function fmtDistanz(m) {
    if (!m && m !== 0) return '–';
    if (m >= 1000) return `${(m / 1000).toFixed(1)}km`;
    return `${Math.round(m)}m`;
  }

  function fmtMinuten(min) {
    if (!min && min !== 0) return '0:00';
    const h = Math.floor(min / 60);
    const m = Math.round(min % 60);
    return `${h}h ${String(m).padStart(2, '0')}min`;
  }

  function fmtZeit(ts) {
    if (!ts) return '';
    return new Date(ts).toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Vienna' });
  }

  if (loading) {
    return (
      <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl p-5 mt-section">
        <div className="animate-pulse space-y-3">
          <div className="h-5 bg-surface-inset rounded w-32" />
          <div className="h-12 bg-surface-inset rounded" />
        </div>
      </div>
    );
  }

  if (!status || !status.app_enabled) return null;

  const istEin = status.status === 'eingestempelt';
  const istPause = status.status === 'in_pause';
  const istAus = status.status === 'ausgestempelt';

  // GPS distance to standort
  let gpsDistanzAnzeige = null;
  if (position && status.standort?.latitude && status.standort?.longitude) {
    const R = 6371000;
    const dLat = (status.standort.latitude - position.latitude) * Math.PI / 180;
    const dLon = (status.standort.longitude - position.longitude) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(position.latitude * Math.PI / 180) * Math.cos(status.standort.latitude * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
    const dist = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    gpsDistanzAnzeige = { meter: Math.round(dist), ausserhalb: dist > (status.standort.radius_meter || 200) };
  }

  return (
    <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden mt-section">
      {/* Header */}
      <div className="px-5 py-3 border-b border-[var(--border-default,#E5E7EB)] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock size={16} className="text-[var(--text-secondary)]" />
          <span className="text-sm font-semibold text-[var(--text-primary)]">{t('stempel.app.title')}</span>
        </div>
        {istEin && status.seit && (
          <span className="text-xs text-[var(--text-tertiary)]">{fmtMinuten(status.arbeitszeit_min)}</span>
        )}
      </div>

      <div className="p-5">
        {/* Status line */}
        <div className="flex items-center gap-2 mb-4">
          <span className={`w-2.5 h-2.5 rounded-full ${istEin ? 'bg-green-500' : istPause ? 'bg-amber-500 animate-pulse' : 'bg-[var(--text-muted)]'}`} />
          <span className="text-sm font-medium text-[var(--text-primary)]">
            {istEin && t('stempel.app.eingestempelt', { zeit: fmtZeit(status.seit) })}
            {istPause && t('stempel.app.inPause', { typ: status.letzter_stempel?.typ === 'mittagspause' ? t('stempel.app.mittagspause', 'Mittagspause') : t('stempel.app.kurzePause', 'Kurze Pause') })}
            {istAus && t('stempel.app.nichtEingestempelt')}
          </span>
        </div>

        {/* Action buttons */}
        {istAus && (
          <button
            onClick={() => handleStempel('arbeit_start')}
            disabled={actionLoading}
            className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-[#338edc] hover:bg-[#2b7bc4] text-white text-sm font-semibold transition-colors disabled:opacity-50"
          >
            {actionLoading === 'arbeit_start' ? (
              <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <LogIn size={16} />
            )}
            {t('stempel.app.jetztEinstempeln')}
          </button>
        )}

        {istEin && (
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => handleStempel('kurze_pause')}
              disabled={actionLoading || !status.optionen?.kurze_pause?.erlaubt}
              className="flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border border-[var(--border-default,#E5E7EB)] hover:border-[#338edc] text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Coffee size={18} className="text-[var(--text-secondary)]" />
              <span className="text-xs font-medium text-[var(--text-primary)]">{t('stempel.app.kurzePause', 'Kurze Pause')}</span>
            </button>
            <button
              onClick={() => handleStempel('mittagspause')}
              disabled={actionLoading || !status.optionen?.mittagspause?.erlaubt}
              className="flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border border-[var(--border-default,#E5E7EB)] hover:border-[#338edc] text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <UtensilsCrossed size={18} className="text-[var(--text-secondary)]" />
              <span className="text-xs font-medium text-[var(--text-primary)]">{t('stempel.app.mittagspause', 'Mittagspause')}</span>
            </button>
            <button
              onClick={() => handleStempel('arbeitsende')}
              disabled={actionLoading}
              className="flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border border-[var(--border-default,#E5E7EB)] hover:border-red-400 text-sm transition-colors disabled:opacity-40"
            >
              <LogOut size={18} className="text-red-500" />
              <span className="text-xs font-medium text-[var(--text-primary)]">{t('stempel.app.feierabend', 'Feierabend')}</span>
            </button>
          </div>
        )}

        {istPause && (
          <button
            onClick={() => handleStempel(status.letzter_stempel?.typ === 'mittagspause' ? 'mittag_ende' : 'pause_ende')}
            disabled={actionLoading}
            className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-[#338edc] hover:bg-[#2b7bc4] text-white text-sm font-semibold transition-colors disabled:opacity-50"
          >
            {actionLoading ? (
              <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <LogIn size={16} />
            )}
            {t('stempel.app.zurueckVonPause', 'Zurück von Pause')}
          </button>
        )}

        {/* GPS Status — nur bei Standortpflicht, sonst wird nichts erhoben (#1813) */}
        {standortPflicht && (
        <div className="mt-3 flex items-center gap-1.5 text-xs">
          <MapPin size={12} className={gpsError ? 'text-red-500' : gpsDistanzAnzeige?.ausserhalb ? 'text-amber-500' : 'text-green-500'} />
          {gpsLoading && <span className="text-[var(--text-tertiary)]">{t('stempel.app.standortErmitteln')}</span>}
          {gpsError && <span className="text-red-500">{gpsError}</span>}
          {!gpsLoading && !gpsError && gpsDistanzAnzeige && (
            <span className={gpsDistanzAnzeige.ausserhalb ? 'text-amber-600' : 'text-[var(--text-tertiary)]'}>
              {gpsDistanzAnzeige.ausserhalb
                ? t('stempel.app.standortWarnungKurz', { distanz: fmtDistanz(gpsDistanzAnzeige.meter), name: status.standort?.name || '' })
                : t('stempel.app.standortOk', { distanz: fmtDistanz(gpsDistanzAnzeige.meter), name: status.standort?.name || '' })
              }
            </span>
          )}
          {!gpsLoading && !gpsError && !gpsDistanzAnzeige && position && (
            <span className="text-[var(--text-tertiary)]">{t('stempel.app.gpsAktiv', 'GPS aktiv')}</span>
          )}
        </div>
        )}

        {/* Toast */}
        {toast && (
          <div className={`mt-3 px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-2 ${
            toast.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' :
            toast.type === 'warning' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
            'bg-red-50 text-red-700 border border-red-200'
          }`}>
            {toast.type === 'success' ? <CheckCircle size={14} /> : toast.type === 'warning' ? <AlertTriangle size={14} /> : <AlertCircle size={14} />}
            {toast.text}
          </div>
        )}

        {/* Heutige Stempel */}
        {status.heutige_stempel?.length > 0 && (
          <div className="mt-4 border-t border-[var(--border-default,#E5E7EB)] pt-3">
            <p className="text-xs font-medium text-[var(--text-secondary)] mb-2">{t('stempel.app.heute', 'Heute')}</p>
            <div className="space-y-1.5">
              {status.heutige_stempel.map(s => (
                <div key={s.id} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[var(--text-primary)]">{fmtZeit(s.zeitpunkt)}</span>
                    <span className="text-[var(--text-secondary)]">
                      {s.typ === 'arbeit_start' ? t('stempel.app.typ.arbeitStart', 'Arbeitsbeginn') :
                       s.typ === 'kurze_pause' ? t('stempel.app.typ.kurzePause', 'Kurze Pause') :
                       s.typ === 'mittagspause' ? t('stempel.app.typ.mittagspause', 'Mittagspause') :
                       s.typ === 'pause_ende' ? t('stempel.app.typ.pauseEnde', 'Pause Ende') :
                       s.typ === 'mittag_ende' ? t('stempel.app.typ.mittagEnde', 'Mittag Ende') :
                       s.typ === 'arbeitsende' ? t('stempel.app.typ.arbeitsende', 'Feierabend') : s.typ}
                    </span>
                  </div>
                  {s.quelle === 'app' && s.gps_distanz_meter != null && (
                    <span className={`flex items-center gap-1 ${s.gps_ausserhalb ? 'text-amber-500' : 'text-green-500'}`}>
                      <MapPin size={10} />
                      {fmtDistanz(s.gps_distanz_meter)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


export default function MaPortal() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/portal/ma-dashboard`, { credentials: 'include' });
      if (res.ok) {
        setData(await res.json());
      }
    } catch (err) {
      console.error('Dashboard laden fehlgeschlagen:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-surface-inset" />
            <div className="space-y-2">
              <div className="h-7 bg-surface-inset rounded w-64" />
              <div className="h-4 bg-surface-inset rounded w-48" />
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-28 bg-surface-inset rounded-card" />)}
          </div>
        </div>
      </div>
    );
  }

  const profil = data?.profil;
  const vorname = profil?.vorname || user?.vorname || t('portal.dashboard.employee');
  const initialen = `${(profil?.vorname?.[0] || '').toUpperCase()}${(profil?.nachname?.[0] || '').toUpperCase()}`;
  const urlaubSummary = data?.urlaub_summary;
  // #1782 — Wert + Sichtbarkeit exakt wie Abwesenheiten-Hero, keine eigene Rechnung.
  const zeigePlanbarKachel = urlaubSummary != null && urlaubSummary.portal_zeige_planbar !== false;
  const urlaubPlanbar = berechnePlanbarAnteile(urlaubSummary).planbar
    .toLocaleString('de-AT', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const zeitenSummary = data?.zeiten_summary;
  const schichtplanWoche = data?.schichtplan_woche;
  const ungeleseneNews = data?.ungelesene_news || 0;
  const unbestaetigteDoks = data?.ungelesene_pflichtdokumente || 0;

  // Build 7-day grid Mon-Sun
  const schichtTage = [];
  if (schichtplanWoche?.woche) {
    const montag = new Date(schichtplanWoche.woche.von + 'T00:00:00');
    const todayStr = new Date().toISOString().slice(0, 10);

    for (let i = 0; i < 7; i++) {
      const d = new Date(montag);
      d.setDate(montag.getDate() + i);
      const datumStr = d.toISOString().slice(0, 10);
      const schicht = schichtplanWoche.schichten?.find(s => s.datum?.slice(0, 10) === datumStr);
      schichtTage.push({
        datum: datumStr,
        tag: t(`portal.dashboard.weekdaysShort.${d.getDay()}`),
        tagNr: d.getDate(),
        istHeute: datumStr === todayStr,
        schicht,
      });
    }
  }

  return (
    <div className="p-8">
      {/* ─── Begrüßungs-Header ─── */}
      <div className="flex items-center gap-3 mb-section">
        {profil?.image_url ? (
          <img src={profil.image_url} alt="" className="w-14 h-14 rounded-full object-cover" />
        ) : (
          <div className="w-14 h-14 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-400 text-xl font-bold">
            {initialen}
          </div>
        )}
        <div>
          <h1 className="text-2xl font-semibold text-content">{getGreeting(t)}, {vorname}!</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-0.5">
            {formatDatum(heute, t)}
            {profil?.berufsbezeichnung && <> · {profil.berufsbezeichnung}</>}
            {profil?.department_name && <> · {profil.department_name}</>}
          </p>
        </div>
      </div>

      {/* ─── Quick Stats ─── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          label={t('portal.dashboard.myTimes')}
          value={zeitenSummary ? `${zeitenSummary.soll_monat}h` : '–'}
          trend={zeitenSummary ? t('portal.dashboard.targetThisMonth') : t('portal.dashboard.viewTargetActual')}
          icon={Clock}
          color="bg-orange-500/10 text-orange-500"
          onClick={() => navigate('/portal/zeiten')}
        />
        {/* #1782 — Desktop-Parität zur Mobile-Kachel: planbar statt Saldo, gleiche Sichtbarkeit */}
        {zeigePlanbarKachel && (
        <KpiCard
          label={t('portal.dashboard.vacationPlannable', 'Zum Planen verfügbar')}
          value={`${urlaubPlanbar} ${t('portal.dashboard.days')}`}
          trend={urlaubSummary.urlaubsjahr_bis
            ? t('portal.dashboard.vacationPlannableUntil', { datum: fmtDateShortP(urlaubSummary.urlaubsjahr_bis), defaultValue: 'bis {{datum}}' })
            : t('portal.dashboard.vacationRequests')}
          icon={Umbrella}
          color="bg-cyan-500/10 text-cyan-500"
          onClick={() => navigate('/portal/urlaub')}
        />
        )}
        <KpiCard
          label={t('portal.dashboard.unreadNews')}
          value={ungeleseneNews}
          trend={ungeleseneNews > 0 ? t('portal.dashboard.newMessages') : t('portal.dashboard.allRead')}
          trendUp={ungeleseneNews === 0 ? true : undefined}
          icon={Newspaper}
          color={ungeleseneNews > 0 ? 'bg-primary-500/10 text-primary-500' : 'bg-emerald-500/10 text-emerald-500'}
          onClick={() => navigate('/portal/news')}
        />
        <KpiCard
          label={t('portal.dashboard.documents')}
          value={data?.dokumente_count || 0}
          trend={unbestaetigteDoks > 0 ? t('portal.dashboard.nUnread', { n: unbestaetigteDoks }) : t('portal.dashboard.allConfirmed')}
          trendUp={unbestaetigteDoks === 0 ? true : undefined}
          icon={FileText}
          color={unbestaetigteDoks > 0 ? 'bg-amber-500/10 text-amber-500' : 'bg-emerald-500/10 text-emerald-500'}
          onClick={() => navigate('/portal/dokumente')}
        />
      </div>

      {/* ─── Stempel-Widget (nur wenn MA stempeln darf) ─── */}
      {profil?.stempel_portal_erlaubt && <StempelWidget t={t} />}

      {/* ─── Schichtplan Widget ─── */}
      <Card
        title={t('portal.dashboard.myShiftPlan')}
        subtitle={t('portal.dashboard.thisWeek')}
        className="mt-section"
        actions={schichtplanWoche?.woche && (
          <span className="text-[10px] text-content-tertiary">
            {new Date(schichtplanWoche.woche.von).toLocaleDateString('de-DE')} – {new Date(schichtplanWoche.woche.bis).toLocaleDateString('de-DE')}
          </span>
        )}
      >
        {schichtTage.length > 0 ? (
          <div className="grid grid-cols-7 gap-2">
            {schichtTage.map(d => {
              const farbe = d.schicht ? getSchichtFarbe(d.schicht.schicht_name) : null;
              return (
                <div
                  key={d.datum}
                  className={`rounded-card p-3 text-center transition-all ${
                    d.istHeute
                      ? 'ring-2 ring-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : ''
                  } ${
                    d.schicht
                      ? `${farbe.bg} border ${farbe.border}`
                      : 'bg-surface-inset border border-transparent'
                  }`}
                >
                  <div className={`text-xs font-medium ${d.istHeute ? 'text-primary-500' : 'text-content-tertiary'}`}>
                    {d.tag}
                  </div>
                  <div className={`text-sm font-semibold mt-1 ${d.istHeute ? 'text-primary-500' : 'text-content'}`}>
                    {d.tagNr}
                  </div>
                  {d.schicht ? (
                    <>
                      <Badge variant={farbe.variant} className="mt-2 text-[10px]">
                        {d.schicht.schicht_name}
                      </Badge>
                      <div className="text-xs text-[var(--text-secondary)] mt-1">
                        {d.schicht.start_zeit?.slice(0, 5)} – {d.schicht.end_zeit?.slice(0, 5)}
                      </div>
                    </>
                  ) : (
                    <div className="text-xs text-content-tertiary mt-2">{t('portal.dashboard.free')}</div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-sm text-content-tertiary italic">
            {t('portal.dashboard.noShiftsPlanned')}
          </div>
        )}
      </Card>

      {/* ─── Neueste News ─── */}
      <Card
        title={t('portal.dashboard.latestNews')}
        subtitle={ungeleseneNews > 0 ? t('portal.dashboard.nUnreadNews', { n: ungeleseneNews }) : undefined}
        className="mt-section"
        actions={
          <Button variant="ghost" size="xs" onClick={() => navigate('/portal/news')}>
            {t('portal.dashboard.showAll')}
          </Button>
        }
      >
        {data?.neueste_news?.length > 0 ? (
          <div className="divide-y divide-line">
            {data.neueste_news.map(news => (
              <NavLink
                key={news.id}
                to={`/portal/news/${news.id}`}
                className="flex items-start gap-3 py-3 hover:bg-surface-hover -mx-card px-card rounded-lg transition-colors group"
              >
                <div className="flex-shrink-0 mt-1">
                  {news.gepinnt ? (
                    <Pin className="w-3.5 h-3.5 text-warning" />
                  ) : news.wichtig ? (
                    <AlertCircle className="w-3.5 h-3.5 text-error" />
                  ) : (
                    <span className={`w-2 h-2 rounded-full inline-block ${news.gelesen ? 'bg-content-dim' : 'bg-primary-500'}`} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-content group-hover:text-primary-500 transition-colors truncate">
                    {news.titel}
                  </div>
                  <div className="text-xs text-content-tertiary mt-0.5">
                    {new Date(news.veroeffentlicht_am).toLocaleDateString('de-DE')}
                    {news.kategorie && news.kategorie !== 'allgemein' && (
                      <Badge variant="default" className="ml-2 text-[10px]">
                        {news.kategorie}
                      </Badge>
                    )}
                  </div>
                </div>
                {!news.gelesen && <div className="w-2 h-2 rounded-full bg-primary-500 mt-2 shrink-0" />}
              </NavLink>
            ))}
          </div>
        ) : (
          <div className="text-sm text-content-tertiary py-4 text-center">{t('portal.dashboard.noCurrentNews')}</div>
        )}
      </Card>

      {/* ─── Urlaub & Zertifikate ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-section">
        <Card hover className="cursor-pointer" onClick={() => navigate('/portal/urlaub')}>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Umbrella size={20} className="text-cyan-500" />
                <div className="text-sm text-[var(--text-secondary)]">
                  {urlaubSummary ? t('portal.dashboard.remainingDays', { year: new Date().getFullYear() }) : t('portal.dashboard.vacationAndRequests')}
                </div>
              </div>
              <div className="text-3xl font-bold text-content">
                {urlaubSummary?.rest ?? '–'}
              </div>
            </div>
            <div className="p-2.5 rounded-card bg-cyan-500/10">
              <Umbrella className="w-5 h-5 text-cyan-500" />
            </div>
          </div>
          {urlaubSummary && urlaubSummary.offene_antraege > 0 && (
            <Badge variant="warning" className="mt-3">
              {urlaubSummary.offene_antraege === 1
                ? t('portal.dashboard.requestPending', { n: 1 })
                : t('portal.dashboard.requestsPending', { n: urlaubSummary.offene_antraege })}
            </Badge>
          )}
        </Card>

        <Card title={t('portal.dashboard.expiringCertificates')}>
          <p className="text-sm text-content-tertiary italic">
            {t('portal.dashboard.expiringCertificatesDesc')}
          </p>
        </Card>
      </div>
    </div>
  );
}
